/**
 * One policy for every repeating background read in the app.
 *
 * The dashboard polls in five places for four different reasons — the case
 * file's collaboration history, the transactions feed, the explorer's latest
 * blocks, the overview's on-chain activity, a case's watch backlog — and each
 * had written its own `setInterval(refetch, ms)`. Written out, that loop is
 * three lines and looks complete; what it is missing is the same three things
 * every time:
 *
 *  - **A failure ceiling.** A loop with no ceiling re-asks a dead endpoint at
 *    the same cadence forever, and the query client retries each tick, so a
 *    5xx-ing read costs three requests per tick indefinitely. `RunsPage` was
 *    the only loop in the app that stopped; its constant is the one below.
 *  - **A hidden-tab pause.** Nothing in this app checked `document.hidden`, so
 *    three background tabs polled on for pixels nobody was looking at. On a
 *    product where reads are metered, that is billed.
 *  - **An unmount guard.** A timeout chain that reschedules itself in a
 *    `finally` keeps going after the view is gone unless something says stop.
 *
 * They are POLICY, not plumbing, which is why they live together rather than
 * being re-derived per call site: "how long do we keep asking a server that is
 * not answering" should have one answer in the product, and a reader should be
 * able to find it.
 *
 * Two shapes consume this. React surfaces use {@link usePollLoop}, which owns
 * the timer. The collaboration transport is not a component and owns its own
 * `setTimeout` chain, so it takes the two numbers ({@link MAX_POLL_FAILURES},
 * {@link pollBackoffMs}) and the {@link pollPaused} check instead — the policy
 * is shared even where the timer cannot be.
 */
import * as React from "react";

/**
 * Consecutive failures tolerated before a loop stops asking.
 *
 * Four, from the run console (`features/runs/RunsPage.tsx`), which is where the
 * number was reasoned about first: long enough to ride out a dropped request or
 * a redeploy, short enough not to hammer a server that is genuinely down. At a
 * 30-second cadence that is two minutes; at the collaboration poll's 2 seconds
 * with backoff, about half a minute.
 *
 * Stopping is deliberate and it is not silent: every loop here is attached to a
 * query whose own error state is already on screen. What stops is the ASKING.
 */
export const MAX_POLL_FAILURES = 4;

/** Longest a backed-off retry ever waits. Matches the collab write path. */
export const POLL_BACKOFF_CEILING_MS = 30_000;

/**
 * Exponential backoff for retry number `failures`, capped.
 *
 * The formula the collaboration transport's WRITE path already used, lifted so
 * its read path can stop being the one loop in the app that retried a failing
 * server at full speed. `failures` is the count of consecutive failures so far;
 * zero (or less) means "not failing", which is the base cadence.
 */
export function pollBackoffMs(
  failures: number,
  baseMs: number,
  ceilingMs: number = POLL_BACKOFF_CEILING_MS,
): number {
  if (failures <= 0) return baseMs;
  return Math.min(baseMs * 2 ** (failures - 1), ceilingMs);
}

/**
 * Whether polling should hold off because nobody is looking at this tab.
 *
 * `visibilityState` rather than `document.hidden` so a non-browser host (tests,
 * SSR) reads as visible instead of throwing. A paused loop keeps its timer and
 * issues no requests, so returning to the tab resumes within one interval.
 */
export function pollPaused(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

export interface PollLoopOptions {
  /** Poll while true. Flipping it false stops the loop and resets its failures. */
  enabled: boolean;
  /** Cadence in ms. A change restarts the loop, so compute it from live mode. */
  intervalMs: number;
  /**
   * What a tick does — normally the surface's `refetch`.
   *
   * Read through a ref, so an inline arrow is fine and does not restart the
   * timer. Nothing is awaited: the wrapped `useQuery` refetch is a nonce bump
   * that resolves nothing, which is exactly why failure is reported through
   * `failed` rather than by rejecting.
   */
  onTick: () => void;
  /**
   * Whether the loop's last read FAILED, as the caller's own error state.
   *
   * Sampled at each tick, so it reports the previous tick's outcome — the loop
   * stops after `maxFailures` consecutive ticks that each found the last one
   * failed. It cannot be inferred here: every polling read in this app goes
   * through `@authe/ui-core/api`'s `useQuery`, whose `refetch` returns `void`
   * and keeps its error on the query.
   */
  failed?: boolean;
  /** Override the shared ceiling. Only a surface with a reason should. */
  maxFailures?: number;
}

/**
 * A repeating refetch that stops on a dead server and pauses on a hidden tab.
 *
 * A timeout chain rather than `setInterval`, because both of those behaviours
 * are decisions about the NEXT tick: an interval cannot decline to fire, and
 * the returning-to-the-tab refresh needs to run one tick early.
 */
export function usePollLoop({
  enabled,
  intervalMs,
  onTick,
  failed = false,
  maxFailures = MAX_POLL_FAILURES,
}: PollLoopOptions): void {
  // Both read through refs so neither restarts the timer: `onTick` is usually a
  // fresh closure per render, and `failed` flips on the very failures this loop
  // is counting — a dependency on either would rebuild the loop, and rebuilding
  // it resets the count, which is precisely the ceiling never being reached.
  const onTickRef = React.useRef(onTick);
  onTickRef.current = onTick;
  const failedRef = React.useRef(failed);
  failedRef.current = failed;

  React.useEffect(() => {
    if (!enabled || intervalMs <= 0) return;
    let cancelled = false;
    let timer: number | undefined;
    let failures = 0;
    /** Set when a hidden tab skipped a tick, so coming back refreshes at once. */
    let missed = false;

    const schedule = (): void => {
      timer = window.setTimeout(tick, intervalMs);
    };

    const tick = (): void => {
      if (cancelled) return;
      if (pollPaused()) {
        // Skipped, not failed: a tab nobody is looking at says nothing about
        // whether the server is answering.
        missed = true;
        schedule();
        return;
      }
      if (failedRef.current) {
        failures += 1;
        // Give up rather than keep asking a server that is plainly not
        // answering. No reschedule: the query's error state stays on screen.
        if (failures > maxFailures) return;
      } else {
        failures = 0;
      }
      onTickRef.current();
      schedule();
    };

    const onVisibility = (): void => {
      if (cancelled || pollPaused() || !missed) return;
      missed = false;
      window.clearTimeout(timer);
      tick();
    };

    document.addEventListener("visibilitychange", onVisibility);
    schedule();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled, intervalMs, maxFailures]);
}
